import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCcw, CheckCircle2, AlertTriangle, Users } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

// Iter 115u — Microsoft Teams presence (Graph, app-only polling).
export default function MsTeamsPresenceCard({ canManage }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/integrations/msgraph/status").then((r) => setSt(r.data)).catch(() => setSt(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const resync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/msgraph/resync");
      toast.success(`Mapped ${data.mapped} staff · ${data.poll?.updated || 0} presences updated`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resync failed");
    } finally { setBusy(false); }
  };

  if (!st) return null;
  const err = (st.resolve_errors || [])[0] || st.last_poll?.error || "";
  const healthy = st.configured && st.live_statuses > 0 && !err;

  return (
    <Card className="border-slate-200 p-6 space-y-4" data-testid="msteams-presence-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">Microsoft Teams — presence</h3>
          <p className="mt-1 text-sm text-slate-500">
            Live Available / Busy / Do not disturb pills in the Who&apos;s in drawer, matched by primary email.
            Graph does not allow app-only presence subscriptions, so Conduit polls in batch every {st.poll_seconds}s.
          </p>
        </div>
        {canManage && (
          <Button size="sm" variant="outline" onClick={resync} disabled={busy} data-testid="msteams-resync">
            <RefreshCcw className={`mr-1.5 h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Resync
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-xs font-medium">
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 ${healthy ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="msteams-state">
          {healthy ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
          {!st.configured ? "Credentials missing" : healthy ? "Connected" : "Configured — not yet live"}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">
          <Users className="h-3.5 w-3.5" /> {st.mapped} staff mapped · {st.live_statuses} live
        </span>
        {st.last_poll?.polled_at && (
          <span className="text-slate-400">Last poll {new Date(st.last_poll.polled_at).toLocaleTimeString()}</span>
        )}
      </div>

      {err && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="msteams-error">
          {err}
        </div>
      )}

      {(st.unmatched || []).length > 0 && (
        <div className="space-y-1" data-testid="msteams-unmatched">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Unmatched staff ({st.unmatched.length})</div>
          <ul className="text-xs text-slate-500">
            {st.unmatched.slice(0, 10).map((u) => <li key={u.user_id}>{u.name} — {u.email}</li>)}
          </ul>
        </div>
      )}
    </Card>
  );
}
